import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CallRecordsService } from '../../../core/services/call-records/call-records.service';
import { AlertService } from '../../../core/services/alert/alert.service';
import { CallResource } from '../../../core/interfaces/communication.interface';
import { CallDetailComponent } from '../call-detail/call-detail.component';
import { ContactsAgendaComponent } from '../contacts-agenda/contacts-agenda.component';
import { SoftphoneWidgetComponent } from '../softphone-widget/softphone-widget.component';

@Component({
  selector: 'app-call-history',
  standalone: true,
  imports: [CommonModule, RouterLink, FormsModule, TranslateModule, CallDetailComponent, ContactsAgendaComponent],
  templateUrl: './call-history.component.html',
  styleUrl: './call-history.component.css',
})
export class CallHistoryComponent implements OnInit {
  calls: CallResource[] = [];
  selectedCall: CallResource | null = null;
  isLoading = true;
  searchText = '';
  filterDirection = '';
  filterStatus = '';
  showAgenda = false;

  private softphoneRef: SoftphoneWidgetComponent | null = null;

  constructor(
    private callRecordsService: CallRecordsService,
    private alertService: AlertService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.loadCalls();
  }

  loadCalls(): void {
    this.isLoading = true;
    const params: any = {};
    if (this.filterDirection) params.direction = this.filterDirection;
    if (this.filterStatus) params.status = this.filterStatus;
    if (this.searchText) params.search = this.searchText;

    this.callRecordsService.getCalls(params).subscribe({
      next: (res) => {
        this.calls = res.data || [];
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      },
    });
  }

  selectCall(call: CallResource): void {
    this.selectedCall = call;
  }

  closeDetail(): void {
    this.selectedCall = null;
  }

  toggleAgenda(): void {
    this.showAgenda = !this.showAgenda;
  }

  closeAgenda(): void {
    this.showAgenda = false;
  }

  onCallFromAgenda(event: { phone: string; name: string }): void {
    this.showAgenda = false;
    // Find softphone widget in the DOM and trigger call
    const softphone = document.querySelector('app-softphone-widget') as any;
    if (softphone && softphone.__ngContext__) {
      // Use a custom event to communicate with the softphone
      window.dispatchEvent(new CustomEvent('softphone-call', { detail: event }));
    } else {
      window.dispatchEvent(new CustomEvent('softphone-call', { detail: event }));
    }
  }

  onFilterChange(): void {
    this.loadCalls();
  }

  formatDuration(seconds: number): string {
    if (seconds <= 0) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  formatDate(dateStr: string | null): string {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    if (isToday) {
      return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  }

  getStatusClass(status: string): string {
    const map: Record<string, string> = {
      answered: 'bg-success', missed: 'bg-danger', busy: 'bg-warning',
      failed: 'bg-danger', initiated: 'bg-info', canceled: 'bg-secondary',
    };
    return map[status] || 'bg-secondary';
  }

  getDispositionClass(disposition: string | null): string {
    if (!disposition) return '';
    const map: Record<string, string> = {
      interested: 'text-success', deal_closed: 'text-success', callback: 'text-info',
      not_interested: 'text-danger', no_answer: 'text-warning', complaint: 'text-danger',
      follow_up: 'text-info', information_sent: 'text-primary',
    };
    return map[disposition] || 'text-secondary';
  }

  getDirectionIcon(direction: string): string {
    return direction === 'inbound' ? 'icon-phone-incoming' : 'icon-phone-outgoing';
  }

  getContactName(call: CallResource): string {
    return call.contact_name || call.client?.name || '';
  }

  getPhoneNumber(call: CallResource): string {
    return call.direction === 'outbound' ? call.to_number : call.from_number;
  }
}
